import { type FC, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';

import { ProductImage } from './Image/ProductImage';
import { ProductBaseTile } from './ProductBaseTile';

import { type MeasurementUnit } from 'dibs-intl/exports/units';
import { type ImageBackgroundColor, type TileSize } from './constants';

import { type BuyerProductTile_viewer$key } from './__generated__/BuyerProductTile_viewer.graphql';
import { type BuyerProductTile_item$key } from './__generated__/BuyerProductTile_item.graphql';
import { type BuyerProductTile_user$key } from './__generated__/BuyerProductTile_user.graphql';

const viewerFragment = graphql`
    fragment BuyerProductTile_viewer on Viewer {
        ...ProductBaseTile_viewer
    }
`;
const itemFragment = graphql`
    fragment BuyerProductTile_item on Item
    @argumentDefinitions(
        isTrade: { type: "Boolean", defaultValue: false }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
        priceBookName: { type: "String" }
    ) {
        ...ProductBaseTile_item
            @arguments(
                isTrade: $isTrade
                pageDisplayEnum: $pageDisplayEnum
                priceBookName: $priceBookName
            )
        ...ProductImage_item
    }
`;

const userFragment = graphql`
    fragment BuyerProductTile_user on User {
        ...ProductBaseTile_user
    }
`;

type Props = {
    viewer: BuyerProductTile_viewer$key | null | undefined;
    item: BuyerProductTile_item$key | null | undefined;
    user: BuyerProductTile_user$key | null | undefined;
    afterDisplay: boolean;
    beforeRender?: () => void;
    currency: string;
    imageBackgroundColor?: ImageBackgroundColor;
    index: number;
    isSponsored?: boolean;
    itemsPerRow: number;
    measurementUnit?: MeasurementUnit;
    onClick?: () => void;
    onContentVisible?: () => void;
    showBrowseSimilar?: boolean;
    showMeasurements?: boolean;
    showPrice?: boolean;
    size: TileSize;
    favoritesComponent?: ReactElement | null;
};

export const BuyerProductTile: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    user: userRef,
    afterDisplay,
    beforeRender,
    currency,
    imageBackgroundColor,
    index,
    isSponsored,
    itemsPerRow,
    measurementUnit,
    onClick,
    onContentVisible,
    size,
    showBrowseSimilar,
    showMeasurements,
    showPrice,
    favoritesComponent,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    return (
        <ProductBaseTile
            viewer={viewer}
            item={item}
            user={user}
            afterDisplay={afterDisplay}
            beforeRender={beforeRender}
            currency={currency}
            index={index}
            isSponsored={isSponsored}
            measurementUnit={measurementUnit}
            onContentVisible={onContentVisible}
            size={size}
            showBrowseSimilar={showBrowseSimilar}
            showMeasurements={showMeasurements}
            showPrice={showPrice}
            favoritesComponent={favoritesComponent}
        >
            <ProductImage
                item={item}
                itemsPerRow={itemsPerRow}
                onClick={onClick}
                size={size}
                imageBackgroundColor={imageBackgroundColor}
            />
        </ProductBaseTile>
    );
};
